import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import showErrorToast from "../../components/CustomComponents/toast/toast";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import useThemeColor from "../../hooks/useThemeColor";

const isPendingPayment = (invoice) => {
  const status = (invoice?.paymentStatus || "").toLowerCase();
  return status === "pending" || status === "partial";
};

function SubscriptionInvoices() {
  const themeColor = useThemeColor();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [companyExpireDate, setCompanyExpireDate] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const [invRes, profileRes] = await Promise.all([
        axiosInstance.get(APIs.GET_COMPANY_INVOICES),
        axiosInstance.get(APIs.GET_COMPANY_PROFILE).catch(() => null),
      ]);

      if (invRes.data?.status && Array.isArray(invRes.data.data)) {
        setInvoices(invRes.data.data);
      } else {
        setInvoices([]);
      }

      const profile = profileRes?.data?.data;
      if (profile) {
        setCompanyExpireDate(
          profile.compExpireDate || profile.CompExpireDate || null
        );
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Failed to load invoices");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      showErrorToast("Payment was cancelled");
      navigate("/subscription-invoices", { replace: true });
    }
  }, [searchParams, navigate]);

  const openInvoiceDetails = async (invoice) => {
    const invoiceID = invoice.invoiceID;
    if (!invoiceID) return;
    try {
      setDetailLoading(true);
      const response = await axiosInstance.get(APIs.GET_INVOICE_BY_ID, {
        params: { invoiceID },
      });
      if (response.data?.status && response.data.data) {
        setSelectedInvoice(response.data.data);
        setShowDetails(true);
      } else {
        showErrorToast(response.data?.message || "Failed to load invoice details");
      }
    } catch (err) {
      console.error(err);
      showErrorToast(
        err.response?.data?.message || "Failed to load invoice details"
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedInvoice(null);
  };

  const handlePayWithDojo = async (invoice) => {
    const invoiceID = invoice.invoiceID;
    if (!invoiceID) return;

    try {
      setPayingId(invoiceID);
      const returnBaseUrl = `${window.location.origin}`;
      const response = await axiosInstance.post(
        APIs.POST_PREPARE_INVOICE_DOJO_CHECKOUT,
        null,
        {
          params: { invoiceID, returnBaseUrl },
        }
      );

      if (response.data?.status && response.data.data?.checkoutUrl) {
        window.location.href = response.data.data.checkoutUrl;
        return;
      }

      showErrorToast(
        response.data?.message || "Unable to start Dojo checkout"
      );
    } catch (err) {
      console.error(err);
      showErrorToast(
        err.response?.data?.message || "Unable to start Dojo checkout"
      );
    } finally {
      setPayingId(null);
    }
  };

  const payBtnStyle = {
    backgroundColor: themeColor,
    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
    border: "none",
    color: "white",
    borderRadius: "8px",
    padding: "6px 12px",
    fontSize: "13px",
    fontWeight: "600",
  };

  if (loading) {
    return <GridSectionLoader />;
  }

  const remaining = selectedInvoice
    ? Math.max(
        0,
        Math.round(
          (parseFloat(selectedInvoice.totalAmount || 0) -
            parseFloat(selectedInvoice.paidAmount || 0)) *
            100
        ) / 100
      )
    : 0;

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h3 className="mb-1">Subscription Invoices</h3>
          <p className="text-muted mb-0">
            Pay pending platform invoices to extend access
            {companyExpireDate
              ? ` · Access until ${moment(companyExpireDate).format("MMM DD, YYYY")}`
              : ""}
          </p>
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            <i className="fa fa-file-invoice fa-2x mb-3 d-block" />
            No invoices found.
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Due</th>
                    <th className="text-end">Subtotal</th>
                    <th className="text-end">VAT</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Paid</th>
                    <th className="text-center">Payment</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.invoiceID}>
                      <td>
                        <strong>{invoice.invoiceNumber}</strong>
                        {invoice.notes ? (
                          <div className="small text-muted">{invoice.notes}</div>
                        ) : null}
                      </td>
                      <td>
                        {moment(invoice.invoiceDate).format("MMM DD, YYYY")}
                      </td>
                      <td>{moment(invoice.dueDate).format("MMM DD, YYYY")}</td>
                      <td className="text-end">
                        {invoice.currency}{" "}
                        {parseFloat(invoice.subtotalAmount || 0).toFixed(2)}
                      </td>
                      <td className="text-end">
                        {invoice.currency}{" "}
                        {parseFloat(invoice.taxAmount || 0).toFixed(2)}
                      </td>
                      <td className="text-end fw-bold">
                        {invoice.currency}{" "}
                        {parseFloat(invoice.totalAmount || 0).toFixed(2)}
                      </td>
                      <td className="text-end text-success">
                        {invoice.currency}{" "}
                        {parseFloat(invoice.paidAmount || 0).toFixed(2)}
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            (invoice.paymentStatus || "").toLowerCase() ===
                            "paid"
                              ? "bg-success"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {invoice.paymentStatus || "Pending"}
                        </span>
                      </td>
                      <td className="text-center">
                        <div className="d-inline-flex gap-1 align-items-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={detailLoading}
                            onClick={() => openInvoiceDetails(invoice)}
                            title="View details"
                          >
                            <i className="fa fa-eye" />
                          </button>
                          {isPendingPayment(invoice) ? (
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={payBtnStyle}
                              disabled={payingId === invoice.invoiceID}
                              onClick={() => handlePayWithDojo(invoice)}
                            >
                              {payingId === invoice.invoiceID ? (
                                <>
                                  <i className="fa fa-spinner fa-spin me-1" />
                                  Starting...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-credit-card me-1" />
                                  Pay now
                                </>
                              )}
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <CustomModal
        title={
          selectedInvoice
            ? `Invoice ${selectedInvoice.invoiceNumber}`
            : "Invoice details"
        }
        isOpen={showDetails && !!selectedInvoice}
        onClose={closeDetails}
        showButton={true}
        showCancelButton={true}
        showSubmitButton={false}
        isCentered={true}
        modalSize="modal-xl"
        maxHeight="85vh"
        minHeight="auto"
      >
        {selectedInvoice && (
          <div className="row g-3">
            <div className="col-lg-8">
              <div className="d-flex flex-wrap gap-3 small text-muted mb-3">
                <span>
                  Date:{" "}
                  {moment(selectedInvoice.invoiceDate).format("MMM DD, YYYY")}
                </span>
                <span>
                  Due: {moment(selectedInvoice.dueDate).format("MMM DD, YYYY")}
                </span>
                <span>
                  Status:{" "}
                  <strong
                    className={
                      (selectedInvoice.paymentStatus || "").toLowerCase() ===
                      "paid"
                        ? "text-success"
                        : "text-danger"
                    }
                  >
                    {selectedInvoice.paymentStatus || "Pending"}
                  </strong>
                </span>
              </div>

              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Product / Subscription</th>
                      <th className="text-end">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedInvoice.items || []).map((item) => {
                      const breakdown =
                        item.entitlementBreakdown ||
                        item.EntitlementBreakdown ||
                        [];
                      return (
                        <tr key={item.invoiceItemID}>
                          <td>
                            <div style={{ whiteSpace: "pre-line" }}>
                              {item.itemDescription}
                            </div>
                            {Array.isArray(breakdown) && breakdown.length > 0 && (
                              <ul className="small text-muted mb-0 mt-1 ps-3">
                                  {breakdown.map((ent, i) => {
                                    const title =
                                      (ent.entitlementTitle ||
                                        ent.EntitlementTitle ||
                                        "").trim() ||
                                      ent.entitlementType ||
                                      ent.EntitlementType ||
                                      "Entitlement";
                                    return (
                                    <li key={i}>
                                      {title}:{" "}
                                      {ent.quantity ?? ent.Quantity}{" "}
                                      {ent.unit || ent.Unit || ""} — £
                                      {parseFloat(
                                        ent.unitPrice ?? ent.UnitPrice ?? 0
                                      ).toFixed(2)}
                                    </li>
                                    );
                                  })}
                              </ul>
                            )}
                            <span className="badge bg-secondary mt-1">
                              {item.itemType}
                            </span>
                          </td>
                          <td className="text-end fw-semibold">
                            {selectedInvoice.currency}{" "}
                            {parseFloat(item.totalPrice || 0).toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="text-muted">Subtotal (ex-VAT)</td>
                      <td className="text-end">
                        {selectedInvoice.currency}{" "}
                        {parseFloat(selectedInvoice.subtotalAmount || 0).toFixed(2)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">VAT</td>
                      <td className="text-end">
                        {selectedInvoice.currency}{" "}
                        {parseFloat(selectedInvoice.taxAmount || 0).toFixed(2)}
                      </td>
                    </tr>
                    {parseFloat(selectedInvoice.discountAmount || 0) > 0 ? (
                      <tr>
                        <td className="text-muted">
                          Discount
                          {selectedInvoice.discountLabel ||
                          selectedInvoice.DiscountLabel
                            ? ` (${
                                selectedInvoice.discountLabel ||
                                selectedInvoice.DiscountLabel
                              })`
                            : ""}
                          {parseFloat(
                            selectedInvoice.discountPercent ||
                              selectedInvoice.DiscountPercent ||
                              0
                          ) > 0
                            ? ` — ${parseFloat(
                                selectedInvoice.discountPercent ||
                                  selectedInvoice.DiscountPercent ||
                                  0
                              ).toFixed(2)}%`
                            : ""}
                        </td>
                        <td className="text-end text-danger">
                          −{selectedInvoice.currency}{" "}
                          {parseFloat(selectedInvoice.discountAmount || 0).toFixed(2)}
                        </td>
                      </tr>
                    ) : null}
                    <tr className="table-dark">
                      <td className="fw-bold">Total</td>
                      <td className="text-end fw-bold">
                        {selectedInvoice.currency}{" "}
                        {parseFloat(selectedInvoice.totalAmount || 0).toFixed(2)}
                        {parseFloat(selectedInvoice.discountAmount || 0) > 0 ? (
                          <div className="small text-decoration-line-through fw-normal opacity-75">
                            {selectedInvoice.currency}{" "}
                            {(
                              parseFloat(selectedInvoice.subtotalAmount || 0) +
                              parseFloat(selectedInvoice.taxAmount || 0)
                            ).toFixed(2)}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  {parseFloat(selectedInvoice.discountAmount || 0) > 0 ? (
                    <div className="mb-2 small">
                      <span className="text-muted">
                        Discount
                        {selectedInvoice.discountLabel ||
                        selectedInvoice.DiscountLabel
                          ? ` (${
                              selectedInvoice.discountLabel ||
                              selectedInvoice.DiscountLabel
                            })`
                          : ""}
                        {parseFloat(
                          selectedInvoice.discountPercent ||
                            selectedInvoice.DiscountPercent ||
                            0
                        ) > 0
                          ? `: ${parseFloat(
                              selectedInvoice.discountPercent ||
                                selectedInvoice.DiscountPercent ||
                                0
                            ).toFixed(2)}%`
                          : ""}
                      </span>
                      <div className="text-danger fw-semibold">
                        −{selectedInvoice.currency}{" "}
                        {parseFloat(selectedInvoice.discountAmount || 0).toFixed(2)}
                      </div>
                    </div>
                  ) : null}
                  <div className="text-muted small mb-1">Total payable</div>
                  <div className="fs-3 fw-bold mb-1" style={{ color: themeColor }}>
                    {selectedInvoice.currency} {remaining.toFixed(2)}
                  </div>
                  {parseFloat(selectedInvoice.discountAmount || 0) > 0 ? (
                    <div className="text-muted text-decoration-line-through small mb-2">
                      {selectedInvoice.currency}{" "}
                      {(
                        parseFloat(selectedInvoice.subtotalAmount || 0) +
                        parseFloat(selectedInvoice.taxAmount || 0) -
                        parseFloat(selectedInvoice.paidAmount || 0)
                      ).toFixed(2)}
                    </div>
                  ) : null}
                  <p className="small text-muted">
                    Summary of all bills on this invoice. Paying applies package
                    entitlements (access, SMS, licenses).
                  </p>
                  {isPendingPayment(selectedInvoice) ? (
                    <button
                      type="button"
                      className="btn w-100"
                      style={payBtnStyle}
                      disabled={payingId === selectedInvoice.invoiceID}
                      onClick={() => handlePayWithDojo(selectedInvoice)}
                    >
                      {payingId === selectedInvoice.invoiceID
                        ? "Starting checkout..."
                        : "Pay now"}
                    </button>
                  ) : (
                    <div className="alert alert-success mb-0 py-2">Paid</div>
                  )}
                  <div className="small text-muted text-center mt-3">
                    Secured by Dojo Payments
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CustomModal>
    </div>
  );
}

export default SubscriptionInvoices;
